import { grey, red } from "@ant-design/colors"
import { Avatar, Row, Space, Table, TableProps, Tooltip, Typography } from "antd"
import { useContext, useState } from "react"
import AddCrew from "./AddCrew"
import { useCrewQuery, useJobQuery, useVisitQuery } from "./apiQueries"
import BulkLaborActions from "./BulkLaborActions"
import { Timesheet } from "./editVisitApi"
import EditVisitContext from "./EditVisitContext"
import { getJobId } from "./editVisitModel"
import { CrewMemberSummary, EnrichedTimesheet, LaborViewItem } from "./labor"
import Lunch from "./Lunch"
import TimePicker from "./TimePicker"
import GpsLog from "../maps/GpsLog"
import WorkareaSelector from "./WorkareaSelector"
import styled from 'styled-components'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCirclePlus, faGlobe, faLock, faTrash, faTrashUndo, faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons"
import { faFlag } from "@fortawesome/pro-duotone-svg-icons"

const { Title, Text, Link } = Typography

const StyledTable = styled((props: React.PropsWithChildren<TableProps<LaborViewItem>>) => <Table {...props} />)`
  && tbody > tr.deleted > td {
    background-color: ${red[1]} !important;
  }
  && tbody > tr.deleted:hover > td {
    background-color: ${red[2]} !important;
    border-radius: 0 !important;
  }
`

type Props = {
  timeZone: string
}

const LaborSection = ({ timeZone }: Props) => {
  const data = useContext(EditVisitContext)
  const defaultLunchBreak = (data.defaultLunchBreakMinutes ?? 30) / 60
  const visitQuery = useVisitQuery(data.visitId)
  const jobId = getJobId(data, visitQuery.data)
  const jobQuery = useJobQuery(jobId, data.visitId)
  const crewQuery = useCrewQuery()
  const [selectedTimesheets, setSelectedTimesheets] = useState<Timesheet[]>([])
  const isLoading = jobQuery.isLoading || crewQuery.isLoading
  const [expandedRowKeys, setExpandedRowKeys] = useState<string[]>([])

  const isSummary = (item: LaborViewItem): item is CrewMemberSummary => (item as CrewMemberSummary).summaryTime !== undefined
  const validSelectedTimesheets = selectedTimesheets.filter(x => data.laborView.some(y => y.id === x.id && !(y as EnrichedTimesheet).isRemoved))

  return <Space direction="vertical" style={{ width: '100%' }}>
    <Title level={5}>Labor</Title>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
      <BulkLaborActions selectedTimesheets={validSelectedTimesheets} />
      <AddCrew />
    </div>
    <StyledTable
      loading={isLoading}
      dataSource={data.laborView}
      rowKey={x => x.id}
      size="small"
      rowClassName={(item) => (item as EnrichedTimesheet).isRemoved ? 'deleted' : ''}
      rowSelection={{
        type: 'checkbox',
        selectedRowKeys: validSelectedTimesheets.map(x => x.id),
        onChange: (_, rows) => setSelectedTimesheets(rows as Timesheet[]),
        getCheckboxProps: (record) => ({
          disabled: isSummary(record) || record.isRemoved || !record.canBeEdited,
        }),
        renderCell: (_checked, record, _index, node) => {
          return isSummary(record) ? <></> : node
        }
      }}
      columns={[
        {
          title: 'Name',
          onCell: (item) => (isSummary(item) ? { colSpan: 6 } : {}),
          render: (_, item) => {
            if (isSummary(item)) {
              return <div style={{ textAlign: 'right' }}><Text strong>Total for {item.name}: {item.summaryTime}</Text></div>
            }
            return <Space size="small">
              <Avatar src={item.crewMemberAvatar}>{item.crewMemberInitials}</Avatar> {item.crewMemberName}
            </Space>
          }
        },
        {
          title: 'Workarea',
          onCell: (item) => ({ colSpan: isSummary(item) ? 0 : 1 }),
          render: (_, item) => {
            if (isSummary(item)) return <></>
            return <WorkareaSelector
              disabled={item.isRemoved || !item.canBeEdited}
              workareas={jobQuery.data?.workareas ?? []}
              selectedWorkareaId={item.workareaId}
              onChange={x => data.dispatch({ type: 'WORKAREA_ID_CHANGED', timesheet: item, payload: x })}
              changed={item.workareaIdChanged}
            />
          }
        },
        {
          title: 'Start Time',
          onCell: (item) => ({ colSpan: isSummary(item) ? 0 : 1 }),
          render: (_, item) => {
            if (isSummary(item)) return <></>
            return <TimePicker
              disabled={item.isRemoved || !item.canBeEdited}
              value={item.startedAt}
              highlight={item.startedAtChanged}
              onChange={x => {
                data.dispatch({ type: 'STARTED_AT_CHANGED', timesheet: item, payload: x ?? '' })
              }}
              dateMismatch={item.startedAtDateMismatch}
              timeZone={timeZone}
            />
          }
        },
        {
          title: 'End Time',
          onCell: (item) => ({ colSpan: isSummary(item) ? 0 : 1 }),
          render: (_, item) => {
            if (isSummary(item)) return <></>
            return <Space direction="horizontal">
              <TimePicker
                disabled={item.isRemoved || !item.canBeEdited}
                allowBlank
                value={item.endedAt}
                highlight={item.endedAtChanged}
                suggested={data.laborChanges.bulkLabor.endAt}
                dateMismatch={item.endedAtDateMismatch}
                onChange={x => {
                  data.dispatch({ type: 'ENDED_AT_CHANGED', timesheet: item, payload: x })
                }}
                timeZone={timeZone}
              />
              {item.hasTimeRangeError &&
                <Tooltip title="End time must be after start time.">
                  <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: red.primary }} />
                </Tooltip>
              }
              {
                item.hasOverlapError &&
                <Tooltip title="This timesheet overlaps another.">
                  <FontAwesomeIcon icon={faFlag} style={{color: red.primary}} />
                </Tooltip>
              }
            </Space>
          }
        },
        {
          title: 'Lunch',
          onCell: (item) => ({ colSpan: isSummary(item) ? 0 : 1 }),
          render: (_, item) => {
            if (isSummary(item)) return <></>
            return <Lunch disabled={item.isRemoved || !item.canBeEdited} lunch={item.lunch} defaultLunch={defaultLunchBreak} highlight={item.lunchChanged} onChange={x =>
              data.dispatch({ type: 'LUNCH_CHANGED', timesheet: item, payload: x })
            } />
          }
        },
        {
          title: 'Total',
          onCell: (item) => (isSummary(item) ? { colSpan: 0 } : {}),
          dataIndex: 'totalTime'
        },
        {
          render: (_, item) => {
            if (isSummary(item)) {
              return <></>
            }
            if (item.isRemoved) {
              return <Row justify="end">
                <Link onClick={() => data.dispatch({ type: 'TIMESHEET_RESTORED', timesheet: item })}>
                  <FontAwesomeIcon icon={faTrashUndo} />
                </Link>
              </Row>
            }
            return <Space size="middle" style={{ fontSize: '1.25em', justifyContent: 'flex-end', width: '100%' }} >
              {item.gpsLog.length > 0 &&
                <Link onClick={() => {
                  if (expandedRowKeys.includes(item.id)) {
                    setExpandedRowKeys([])
                  }
                  else {
                    setExpandedRowKeys([item.id])
                  }
                }}
                >
                  {item.gpsLog.some(x => x.isFlagged) ? <FontAwesomeIcon icon={faFlag} /> : <FontAwesomeIcon icon={faGlobe} />}
                </Link>
              }

              <Link onClick={() => data.dispatch({ type: 'ADD_TIMESHEET', timesheet: item })}><FontAwesomeIcon icon={faCirclePlus} /></Link>
              {item.canBeEdited && <Link onClick={() => data.dispatch({ type: 'REMOVE_TIMESHEET', timesheet: item })}><FontAwesomeIcon icon={faTrash} style={{color: red.primary}} /></Link>}
              {!item.canBeEdited && <Tooltip title="This timesheet may not be edited."><FontAwesomeIcon icon={faLock} style={{color: grey.primary}} /></Tooltip>}
            </Space>
          }
        }
      ]}
      expandable={{
        expandedRowRender: (x) => {
          return <GpsLog googleMapsApiKey={data.googleMapsApiKey} logs={(x as Timesheet).gpsLog} hideTitle />
        },
        showExpandColumn: false,
        expandedRowKeys,
        indentSize: 0,
      }}
      pagination={false}
    />
  </Space >
}
export default LaborSection
